import {
	CHUNK_SIZE,
	ChunkNetworkError,
	createChunkedUploadDriver,
	formatFileSize,
	getChunkSize,
	hasContiguousParts,
	logStorageEstimate as logStorageEstimateWithTelemetry,
	MultipartUploadGoneError,
	normalizeLegacyResumeChunkMetadata,
	processWithAdaptiveConcurrency,
	processWithConcurrency,
	uploadTargetChunkUrls,
	type DriverUploadOutcome,
	UploadCancelledError
} from "@uploads";
import type { FileCategory, FileUploadSession } from "@/db/fileUploadDb";
import {
	createAdminLegacyResumeLeaseScope,
	adminUploadTelemetry,
	AimdConcurrencyController,
	AmbiguousUploadTargetError,
	createAdminChunkedUploadDriverPorts,
	FileUnreadableError,
	inferFileCategory,
	isFileReadError,
	isTransientUploadNetworkError,
	isUploadDeferredError,
	isUploadLost,
	isUploadSessionDeliverableByResume,
	probeMultipartParts,
	TruncatedPartsError,
	UploadDeferredError,
	UploadLostError
} from "@/services/uploads/adminChunkedUploadDriver";
import { uploadFailureLogDetails } from "@/services/uploads/uploadLogRedaction";
import { createAdminSessionStore } from "@/services/uploads/adminSessionStore";
import type { MultipartUploadTarget } from "@/types/uploadTarget";

export {
	AimdConcurrencyController,
	AmbiguousUploadTargetError,
	CHUNK_SIZE,
	ChunkNetworkError,
	FileUnreadableError,
	getChunkSize,
	hasContiguousParts,
	inferFileCategory,
	isFileReadError,
	isTransientUploadNetworkError,
	isUploadDeferredError,
	isUploadLost,
	isUploadSessionDeliverableByResume,
	MultipartUploadGoneError,
	probeMultipartParts,
	processWithAdaptiveConcurrency,
	processWithConcurrency,
	TruncatedPartsError,
	UploadCancelledError,
	UploadDeferredError,
	UploadLostError
};

export type FileUploadProgressCallback = (bytesUploaded: number) => void;

export const adminChunkedUploadDriver = createChunkedUploadDriver(createAdminChunkedUploadDriverPorts());

export async function logStorageEstimate(): Promise<void> {
	await logStorageEstimateWithTelemetry(adminUploadTelemetry);
}

export async function persistFileToIndexedDB(
	file: File,
	target: MultipartUploadTarget,
	conversationId: string,
	fileCategory?: FileCategory,
	projectChecklistItemId?: string,
	batchId?: string,
	onPreparationProgress?: (chunksPersisted: number, totalChunks: number) => void,
	captureEvidence?: Record<string, unknown>
): Promise<string> {
	return adminChunkedUploadDriver.persistFileToIndexedDB({
		file,
		target,
		conversationId,
		batchId,
		fileCategory: fileCategory ?? inferFileCategory(file.type),
		projectChecklistItemId,
		onPreparationProgress,
		captureEvidence
	});
}

export async function uploadFile(
	file: File,
	target: MultipartUploadTarget,
	onProgress: FileUploadProgressCallback,
	conversationId?: string,
	fileCategory?: FileCategory,
	cancelSignal?: AbortSignal,
	batchId?: string
): Promise<DriverUploadOutcome> {
	const totalChunks = uploadTargetChunkUrls(target).length;
	console.info("[Upload] Starting chunked upload", {
		fileSize: file.size,
		totalChunks,
		chunkSize: CHUNK_SIZE
	});
	return adminChunkedUploadDriver.uploadFile({
		file,
		target,
		onProgress,
		conversationId,
		batchId,
		fileCategory: fileCategory ?? inferFileCategory(file.type),
		cancelSignal
	});
}

export async function resumeFileUpload(
	session: FileUploadSession,
	unuploadedChunks: { chunkIndex: number; size: number }[],
	allChunks: { chunkIndex: number; isUploaded: number; etag: string | null }[],
	chunkUrls: string[],
	loadChunkBuffer: (sessionId: string, chunkIndex: number) => Promise<ArrayBuffer | null | undefined>,
	onProgress?: FileUploadProgressCallback,
	cancelSignal?: AbortSignal
): Promise<void> {
	await adminChunkedUploadDriver.resumeFileUpload(
		createAdminResumeRequest(session, unuploadedChunks, allChunks, chunkUrls, loadChunkBuffer, onProgress, cancelSignal)
	);
}

function createAdminResumeRequest(
	session: FileUploadSession,
	unuploadedChunks: { chunkIndex: number; size: number }[],
	allChunks: { chunkIndex: number; isUploaded: number; etag: string | null }[],
	chunkUrls: string[],
	loadChunkBuffer: (sessionId: string, chunkIndex: number) => Promise<ArrayBuffer | null | undefined>,
	onProgress?: FileUploadProgressCallback,
	cancelSignal?: AbortSignal
): Parameters<typeof adminChunkedUploadDriver.resumeFileUpload>[0] {
	const normalizedChunkMetadata = normalizeLegacyResumeChunkMetadata({
		fileSize: session.fileSize,
		chunkSize: CHUNK_SIZE,
		allChunks,
		unuploadedChunks
	});
	return {
		session,
		...normalizedChunkMetadata,
		chunkUrls,
		chunkSource: {
			async read(chunkIndex) {
				const buffer = await loadChunkBuffer(session.id, chunkIndex);
				return buffer?.byteLength ? { ok: true, blob: new Blob([buffer]) } : { ok: false, kind: "missing" };
			}
		},
		onProgress,
		createTruncatedPartsError: (uploadedPartCount) =>
			new TruncatedPartsError(
				`Resume of "${session.fileName}" aborted before completion: have ${uploadedPartCount}/${session.totalChunks} part etags ` +
					`(expected contiguous 1..${session.totalChunks}). Refusing to complete a truncated object; session stays recoverable.`
			),
		cancelSignal,
		leaseScope: createAdminLegacyResumeLeaseScope(session.id, session.uploadOwnerClaimId)
	};
}

/**
 * The identity-fenced local discard.
 *
 * R-1 ("there is no destroy", owner directive 2026-08-26) removed its ONE production caller — the
 * dialog's `UPLOAD_LOST` branch, which deleted an intact durable session and its retained chunk
 * buffers on a server verdict about the multipart TARGET. Do not wire it back to any server-loss
 * classification: that path now reinitializes the session from those buffers
 * (`adminSessionStore.resetForReinitialization`), and the only sanctioned discard left is the
 * bytes-gone cleanup the driver reaches after `verifyReinitializable` proves the buffers are gone.
 * Kept because it is still the correct mechanism for an explicitly dismissed session, and because
 * its captured-identity fencing is the contract every future discard site must copy.
 */
export async function discardLostUploadSession(
	sessionId: string | null | undefined,
	uploadId: string | null | undefined,
	// The dismissing entry's own captured lease identity, when it has one. Callers without it fall back to
	// the best-effort discard, which cannot distinguish a successor claim from the row it observed.
	expectedIdentity?: { uploadOwnerId?: string; uploadOwnerClaimId?: string }
): Promise<void> {
	if (!sessionId) {
		try {
			console.warn("[Upload] Cannot discard lost upload session: missing session ID", { uploadId });
		} catch {}
		return;
	}
	if (!uploadId) {
		try {
			console.warn("[Upload] Cannot discard lost upload session: missing upload ID", { sessionId });
		} catch {}
		return;
	}
	// The driver's `discardLostSession` is a pure delegation to this same store method and carries no
	// identity parameter, so an identity-bearing dismissal calls the store directly rather than widening
	// the shared upload port (contact-async implements it too).
	const discard =
		expectedIdentity?.uploadOwnerClaimId !== undefined
			? createAdminSessionStore().deleteSessionByIdIfUploadIdMatches(sessionId, uploadId, expectedIdentity)
			: adminChunkedUploadDriver.discardLostSession({ sessionId, uploadId });
	await discard.catch((error) => {
		try {
			console.warn("[Upload] Could not discard lost upload session", {
				sessionId,
				uploadId,
				...uploadFailureLogDetails(error)
			});
		} catch {}
	});
}
